import React, { useEffect, useState } from "react";
import Navbar from "../components/Cripsr/NavbarCrispr";
import NarrativeSection from "../components/phones/NarrativeSection";
import EndOfStoryNav from "../components/phones/EndOfStoryNav";
import BecauseScienceFooter from "../components/phones/BecauseScienceFooter";
import RevealInfo from "../components/phones/RevealInfo";

// --- IMAGES ---
import heroPattern from "../assets/images/hero/Hero Images-01.png";
import gutThumb from "../assets/images/hero/Hero Images-03.png";
import phonesThumb from "../assets/images/hero/Hero Images.png";
import siliconThumb from "../assets/images/hero/Hero Images-02.png";
import foeThumb from "../assets/images/hero/Hero Images-04.png"
import rechargingThumb from "../assets/images/hero/Hero Images-05.png";
import RevealImageToggle from "../components/phones/RevealImageToggle";
import ArticleCredits from "../components/phones/ArticleCredits";

// Images rendered
import ClearviewAI from "../assets/images/privacy/ClearviewAI.png";
import ObfuscatedTest1 from "../assets/images/privacy/Obfustacted Images Cropped_Test 1.png";
import ObfuscatedTest1Obf from "../assets/images/privacy/Obfustacted Images Cropped_Test 1 - Obfuscated.png";
import ObfuscatedTest3 from "../assets/images/privacy/Obfustacted Images Cropped_Test 3.png";
import ObfuscatedTest3Obf from "../assets/images/privacy/Obfustacted Images Cropped_Test 3 - Obfuscated.png";
import ObfuscatedTest4 from "../assets/images/privacy/Obfustacted Images Cropped_Test 4.png";
import ObfuscatedTest4Obf from "../assets/images/privacy/Obfustacted Images Cropped_Test 4 - Obfuscated.png";
import PrivacyPost from "../assets/images/privacy/PrivacyPost.png";
import Rakibul from "../assets/images/privacy/RakibulHasan.jpg";

const navLinks = [
    { label: "We love our phones", href: "/phones" },
    { label: "From stone to silicon", href: "/phones" },
    { label: "Friend or foe", href: "/phones/friendorfoe" },
    { label: "The privacy burden", href: "/phones/privacyburden" },
    { label: "Gut check", href: "/phones/gutcheck" },
    { label: "Recharging", href: "/phones/recharging" },
];


const dataRights = [
    "Names and passwords",
    "Photos and videos",
    "Links to photos taken, tagged in or liked",
    "Text message history",
    "Address book contacts",
    "App usage",
    "Device info, operating system and carrier",
    "Data on connected devices",
    "Facial recognition data",
    "Geolocation data",
    "Transactional data from Meta products and services",
];

export default function PrivacyBurdenPage() {
    const [isShrunk, setIsShrunk] = useState(false);
    useEffect(() => {
        const onScroll = () => setIsShrunk(window.scrollY > 0);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <div className="relative w-full bg-white">
            {/* Navbar */}
            <Navbar isShrunk={isShrunk} navLinks={navLinks} />


            {/* HERO + audio + opener */}
            <NarrativeSection
                id="foe"
                containerWidth="max-w-md"
                padY="py-10"
                centeredImage={{
                    src: heroPattern,
                    alt: "Friend or foe hero",
                    widthClass: "w-[90%] sm:w-[85%]",
                }}
                // no big heading — matches your reference layout
                content={[
                    // inline audio block (keeps components unchanged)
                    <div key="audio" className="text-center mb-8">
                        <p className="uppercase text-gray-500 text-xs tracking-wide mb-2">
                            Listen to this episode
                        </p>
                        <div className="flex justify-center">
                            <audio controls className="w-full max-w-md">
                                <source src="/audio/friend-or-foe.mp3" type="audio/mpeg" />
                                Your browser does not support the audio element.
                            </audio>
                        </div>
                    </div>,

                    `It can happen in an instant.`,
                    `Done on a lark.`,
                    `Maybe on a dare.`,
                    `It can be an act of spontaneity.`,
                    `A reaction to a prod by a friend to share something online.`,
                    `You are sure it is only going to your friends. You really want them to know, to be part of your experience.`,
                    `It often is a picture, but it also could be a comment. Or a critique.`,
                    `You press “post.”`,

                ]}
            />

            <RevealInfo
                imageSrc={PrivacyPost} // or import above
                imageAlt="Example social post"
                buttonLabel="See what info you share"
                panelTitle="Data Rights Forfeited"
                panelList={dataRights}
                className="mx-auto"
            />
            <NarrativeSection
                containerWidth="max-w-md"
                padY="py-8"
                content={[

                    `And the control you had over your data is gone. Not only the picture, but your location, the date, the time, the topic, all are no longer controlled by you. Instead, the data spiral out into the metaverse with nary a tether to you, the person who originated it.`,
                    `It is that sense of letting go, that uncertainty in the flow of information and who may access it, use it, sell it, that concerns Rakibul Hasan the most. Hasan studies on-line privacy and the temptations facing social media users. He is an engineer, but he is equally interested in social phenomena – how we act, how we respond, how decide what we do on social media. And he brings those perspectives together to figure out the best ways to protect our information, our data, our privacy, while we roam the social media landscape.`,
                    `Hasan sees value in social media. It brings us closer together and strengthens our social fabric, he says, feeding an innate evolutionary desire.`,
                    `But Hasan also sees danger in social media. He knows that even savvy users continually face the choice of willfully sharing data with friends and family with the reality that to share with those close to you also means you could be sharing with others, those who you don’t want to share with. Those who see your data as a commodity. Those who see you as a faceless fountain of that data. Those who you don’t know.`,
                    `“We share billions of images every day on social platforms,” Hasan says. But he quickly asks: Who controls those images? Who are the gatekeepers? Who decides where they go? Who is depicted in them? Do the people in the images want the images willfully passed around by others?`,
                    {
                        type: "quote",
                        content:
                            "... this is really unfortunate because as consumers, we have the burden to protect our data.",
                        tone: "primary",
                        align: "left",
                        className: "text-[18px] sm:text-lg",
                    },
                    `“We share billions of images every day on social platforms,” Hasan says. But he quickly asks: Who controls those images? Who are the gatekeepers? Who decides where they go? Who is depicted in them? Do the people in the images want the images willfully passed around by others?`,
                    `Hasan, anassistant professor of engineering in the School of Computing and Augmented Intelligence at Arizona State University, develops methods and tools that can be used to help people protect their privacy while venturing out into the vast and, highly unregulated, social media landscape. But he also wants to develop tools and methods that will protect others privacy as well. Without these tools, he believes, the only other protective choice you have is to not participate.`,



                ]}
            />
            <NarrativeSection
                containerWidth="max-w-md"
                padY="py-8"

                centeredImage={{
                    src: Rakibul,
                    alt: "Phones impacting our relationships",
                    widthClass: "w-[50%] sm:w-[40%]",
                }}
                content={[
                    `“The obvious way to minimize this [risk] is by not using any kind of digital technology,” he explains. “But it is not possible to get rid of them, because they come with lots of benefits and we are social creatures. We crave these kinds of social interactions, these connections with other people. And this is really unfortunate because as consumers, we have the burden to protect our data.”`,
                    `So, he says, when you are asked about permissions from a new site or a new application, pay attention to them and respond knowing you are your last line of defense safeguarding your privacy and the privacy of other people.`,

                    <h3 key="carrot" className="text-blue-700 font-semibold text-xl sm:text-3xl mt-4">
                        A picture is worth a thousand bucks
                    </h3>,
                    `In general, when you post an image online, even among friends, you basically forfeit your rights to it. A friend, for example, can repost it on a platform, easily putting it outside your circle of friends. Or, the platform of your choice allows third parties to come in and see and use the images on it. The point is, many platforms that control the image only sees the person who posted it as being the person who “owns” it. So once you post an image and it is reposted, your rights are likely lost, Hasan says.`,
                    `When an image is posted, then reposted, then reposted again and again, it is easy to see that the original owner of that image melts away, but that person still has the most at stake.`,
                    `“Anyone can use them or access them,” Hasan says of the reposted images, and there are many companies that have used them in unsettling ways.`,
                    `Clearview AI is a good example. Clearview AI trained algorithms on publicly available online data, scraping that data and putting them into a database. Included in this information could be images of people, a person’s name, place of employment, home address, hobbies, even friends and acquaintances, anything publicly accessible on social media platforms. Clearview AI sold licenses to their database, most notably to law enforcement agencies who used them to help find people for open criminal cases.`,
                    `“It was heavily criticized by privacy researchers, activists, even other companies like Facebook or Google,” Hasan says.`,
                    ` “They collected, they scraped all of those publicly available images, billions of images, fromonline, social platforms or other internet websites” and put them into their database, Hasan says. “Then they created a service where someone could upload an image of a person and then ask, who is that person? Where do they live? All kinds of details about that person,” much of which is available on the internet or shared on social media.”`,
                    // Photo with caption
                    <figure key="photo" className="flex justify-center mb-6">
                        <div className="w-[95%] sm:w-[95%]">
                            <img
                                src={ClearviewAI}
                                alt="Douglas Kenrick and son David Laughing-Kenrick"
                                className="w-full rounded-sm shadow-sm"
                            />
                            <figcaption className="mt-2 text-sm text-gray-600 italic">
                                Clearview AI - Amr Alfiky for The New York Times
                            </figcaption>
                        </div>
                    </figure>,
                    `“It’s creepy what they’re doing,” Al Gidari, a privacy professor at Stanford Law School told the New York Times, “but there will be many more of these companies. There is no monopoly on math. Absent avery strong federal privacy law, we’re all screwed.”`,
                    `Hasan adds that there already have been cases where law enforcement agencies using Clearview AI “arrested the wrong person because the facial recognition algorithm made mistakes in identifying people.”`,
                    `And that's not the end of the story.`,
                    `The database at Clearview AI was hacked. So now all of that data, all of those images and associated data tied to those images can be accessed on the dark web by nearly anyone with good or bad intentions. No one knows who might have access to them or who might be using these images, these data.`,
                    `“They can be on the black market,” Hasan says. “They can be used for any purpose, by anyone.”`,

                    <h3 key="carrot" className="text-blue-700 font-semibold text-xl sm:text-3xl mt-4">
                        Protect yourself, protect others
                    </h3>,
                    `So how do you protect yourself from this situation? Be mindful, Hasan says. Be mindful of yourself and be mindful of others.`,
                    `“My research is mostly focused on how to make people more privacy respecting towards other people,” he explains. “So when I’m sharing my friend’s data, when I’m sharing all the contacts I have in my mobile device with some application I am not only harming my privacy, but also all the other people whose data I, I do not own, but I have.”`,
                    `“We are trying to understand, we are trying to measure how much one person values another person’s privacy,” he adds. “We are trying to understand how do we approach this for each individual person, what kinds of interventions do we need and what kinds of tools do we have to enhance their privacy.”`,
                    `Taking images on your phone and being mindful to use the tools it has to block out sensitive information or the faces of others – called obfuscation of the image – and using the tools available on social platforms like Instagram and Facebook to further secure your data all contribute to the sound practices of protecting your privacy and that of others.`,


                ]}
            />
            <RevealImageToggle
                options={["Original", "Obfuscated"]}
                imagesByOption={{
                    Original: ObfuscatedTest1,
                    Obfuscated: ObfuscatedTest1Obf,
                }}
                description="Hasan’s image filtering process maintains the image’s general aesthetic while removing micro-features that most facial recognition models rely on."
                frameVariant="offset-fill-right"
                heightPx={520}
            />

            <NarrativeSection
                containerWidth="max-w-md"
                padY="py-8"
                content={[

                    `To aid this effort, Hasan has been working on “artistic image and video transforms” to improve the visual aesthetics of an image while enhancing the privacy of the user. It is within this balance between shielding your privacy but keeping the product (image) appealing and usable where much of the focus of his work lies.`,
                    `He says a straightforward way to enhance privacy would be to mask whatever sensitive information the image holds. But a blanket masking protection would severely impact the visual aesthetic of that image – the reason why you posted it in the first place.`,
                    `“And people will not use it because they want social interaction from other people when they share it online,” Hasan says. “So we are trying to balance this trade off, like trying to find more appeasing image filters to obfuscate image content so that sensitive information is obfuscated but the image also retains its visual aesthetics.”`,


                ]}
            />
            <RevealImageToggle
                options={["Original", "Obfuscated"]}
                imagesByOption={{
                    Original: ObfuscatedTest3,
                    Obfuscated: ObfuscatedTest3Obf,
                }}
                description="Our brains naturally seek out the general features that make up a face, such as two eyes, a nose and a mouth, while also blurring out faces in the background or in our peripheral vision."
                frameVariant="offset-fill-right"
                heightPx={520}
            />

            <NarrativeSection
                containerWidth="max-w-md"
                padY="py-8"
                content={[

                    `For mobile applications, Hasan’s group has identified app image filters and image transforms that are implemented in mobile applications like filters for Snapchat or Instagram but he knows these tools will need to be accepted by platform developers.`,
                    `“We know that this is the best way so far to protect this information,” he says.`,
                    `Hasan now is working with social media developers on a browser-based plug in where when a user goes to a platform, like Snapchat, via their browser and they try to upload an image there will be a tool that will assess the image, try to understand what is going on in the image and try to automatically detect sensitive content and propose ways to block or mask that content.`,
                    `What sets apart Hasan’s work from others in this field is that he melds the common operable sensibility and ease of use of an engineer with a desire to understand human behavior. He regularly collaborates with psychology professors, sociologists, and machine learning researchers in his quest to understand motivation, temptation, empathy, security. His engineering side recognizes that he can develop a wonderful tool to protect privacy, but if it is not intuitive he has lost the battle.`,
                    `“If no one can use it, it is useless,” he says.`


                ]}
            />

            <RevealImageToggle
                options={["Original", "Obfuscated"]}
                imagesByOption={{
                    Original: ObfuscatedTest4,
                    Obfuscated: ObfuscatedTest4Obf,
                }}
                frameVariant="offset-fill-right"
                heightPx={520}
            />

            <NarrativeSection
                containerWidth="max-w-md"
                padY="py-8"
                content={[

                    `One way of making people more mindful of privacy concerns is to remind them when they are using social media or out on the internet that they may be edging closer to danger. These “interventions” act as a course correction for the user. But pushing users too hard in any direction, including towards more privacy, Hasan says, can have a deleterious effect.`,
                    `In one study with colleagues out of the University of Indiana “we found that when we warned people about a privacy evaluation, they actually wanted to share more,” he says. “We found, at least in our case, our intervention backfired.”`,
                    `So why would somebody want to share more as a result of being warned of privacy issues? Hasan says there are several reasons.`,
                    `The most popular one as documented in psychological literature is a reaction to authority.`,
                    `“People want to think that whatever decision they make is their own choice,” Hasan explains. “They don’t want to be told what to do. This kind of reactance behavior is pretty common.”`,
                    `In the past, generic interventions have been used, something like do not enter this page because it is not secure. These types of warnings either don't work or they have a very limited effect.`,

                    {
                        type: "quote",
                        content:
                            "The truth holds that more information sharing means more sensitive information sharing.",
                        tone: "primary",
                        align: "left",
                        className: "text-[18px] sm:text-lg",
                    },
                    `“We need to work harder coming up with different types of interventions that will be more effective,” he says adding that researchers are now “trying to create personalized interventions, because different people can be motivated in different ways, different people have different barriers to doing the right thing. We need to understand how to best match those people, different subgroups of people, and then try to come up with the appropriate intervention. This is also probably true in terms of respecting other people’s privacy. This is what we are working on right now.”`,
                    `“We are trying to understand, we are trying to measure, how much one person values another person’s privacy,” he adds. “We are trying to understand how should we approach for each individual person what kind of interventions we need and what kind tools we have to provide to enhance their privacy.”`,
                    `With more and more people using social media and easier access to it, fueled by phones in most anyone’s hands you are talking about an enormous and growing market for which to customize. And the rate at which new information is coming at us, demanding our attention but requiring less of a thoughtful response in lieu of a simple reaction opens us to losing focus on what is at stake or even where we may have wandered in social media. To those temptations, Hasan has simple words of advice:`,
                    `“The truth holds that more information sharing means more sensitive information sharing,” he says.`


                ]}
            />
            <ArticleCredits
                lines={[
                    "Written by by Skip Derra",
                    "Illustrations by Alex Davis",
                    "Audio narration by Nikai Salcido",
                    "Sound design by Christopher Goulet",
                ]}
            />

            {/* --- NEXT + MORE SECTIONS --- */}
            <EndOfStoryNav
                nextChapter={{
                    href: "/phones/gutcheck",
                    title: "Gut check",
                    imageSrc: gutThumb,
                    imageAlt: "Gut check banner",
                }}
                more={[
                    { href: "/phones", title: "We love our phones", imageSrc: phonesThumb },
                    { href: "/phones/stoneandsilicon", title: "From stone to silicon", imageSrc: siliconThumb },
                    { href: "/phones/friendorfoe", title: "Friend or foe", imageSrc: foeThumb },
                    { href: "phones/recharging", title: "Recharging", imageSrc: rechargingThumb },
                ]}
            />

            {/* --- FOOTER --- */}
            <BecauseScienceFooter />
        </div>
    );
}
